import { apiClient } from './api';

export type LeaveType = '연차' | '오전 반차' | '오후 반차' | '휴일 근무' | '대체 휴가';

export type RequestStatus = '승인' | '상신중' | '반려' | '취소 요청 중' | '취소됨';

export type CancelRequestStatus = '취소 요청 중' | '취소 승인' | '취소 반려';

export interface LeaveRequest {
  id: number;
  type: LeaveType;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  status: RequestStatus;
  requestDate: string;
  approver?: string;
  rejectReason?: string;
  employee: {
    id: number;
    name: string;
    department: string;
    position: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LeaveBalance {
  id: number;
  year: number;
  total: number;
  used: number;
  remaining: number;
  employee: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LeaveCancelRequest {
  id: number;
  cancelReason: string;
  status: CancelRequestStatus;
  requestDate: string;
  approver?: string;
  rejectReason?: string;
  leaveRequest: LeaveRequest;
  employee: {
    id: number;
    name: string;
    department: string;
    position: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateCancelRequestDto {
  cancelReason: string;
  employeeId: number;
  requestDate?: string;
}

export interface CreateLeaveRequestDto {
  type: LeaveType;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  employeeId: number;
  requestDate?: string;
}

export interface UpdateLeaveRequestDto extends Partial<CreateLeaveRequestDto> {
  status?: RequestStatus;
  approver?: string;
  rejectReason?: string;
}

export const attendanceApi = {
  // Leave Requests API
  createLeaveRequest: (leaveRequest: CreateLeaveRequestDto): Promise<LeaveRequest> => {
    return apiClient.post<LeaveRequest>('/attendance/leave-requests', leaveRequest);
  },

  getAllLeaveRequests: (): Promise<LeaveRequest[]> => {
    return apiClient.get<LeaveRequest[]>('/attendance/leave-requests');
  },

  getLeaveRequestsByEmployee: (employeeId: number): Promise<LeaveRequest[]> => {
    return apiClient.get<LeaveRequest[]>(`/attendance/leave-requests?employeeId=${employeeId}`);
  },

  getLeaveRequestsByStatus: (status: RequestStatus): Promise<LeaveRequest[]> => {
    return apiClient.get<LeaveRequest[]>(`/attendance/leave-requests?status=${status}`);
  },

  getLeaveRequestById: (id: number): Promise<LeaveRequest> => {
    return apiClient.get<LeaveRequest>(`/attendance/leave-requests/${id}`);
  },

  updateLeaveRequest: (id: number, leaveRequest: UpdateLeaveRequestDto): Promise<LeaveRequest> => {
    return apiClient.patch<LeaveRequest>(`/attendance/leave-requests/${id}`, leaveRequest);
  },

  deleteLeaveRequest: (id: number): Promise<void> => {
    return apiClient.delete<void>(`/attendance/leave-requests/${id}`);
  },

  approveLeaveRequest: (id: number, approver: string): Promise<LeaveRequest> => {
    return apiClient.patch<LeaveRequest>(`/attendance/leave-requests/${id}/approve`, { approver });
  },

  rejectLeaveRequest: (id: number, rejectReason: string): Promise<LeaveRequest> => {
    return apiClient.patch<LeaveRequest>(`/attendance/leave-requests/${id}/reject`, { rejectReason });
  },

  // Leave Balance API
  getAllLeaveBalances: (): Promise<(LeaveBalance & { employeeName: string; joinDate: string })[]> => {
    return apiClient.get<(LeaveBalance & { employeeName: string; joinDate: string })[]>('/attendance/leave-balances/all');
  },

  getLeaveBalance: (employeeId: number): Promise<LeaveBalance | null> => {
    return apiClient.get<LeaveBalance | null>(`/attendance/leave-balance/${employeeId}`);
  },

  // Cancel Request API
  createCancelRequest: (leaveRequestId: number, cancelRequest: CreateCancelRequestDto): Promise<LeaveCancelRequest> => {
    return apiClient.post<LeaveCancelRequest>(`/attendance/leave-requests/${leaveRequestId}/cancel-request`, cancelRequest);
  },

  getAllCancelRequests: (): Promise<LeaveCancelRequest[]> => {
    return apiClient.get<LeaveCancelRequest[]>('/attendance/cancel-requests');
  },

  getCancelRequestsByEmployee: (employeeId: number): Promise<LeaveCancelRequest[]> => {
    return apiClient.get<LeaveCancelRequest[]>(`/attendance/cancel-requests?employeeId=${employeeId}`);
  },

  getCancelRequestsByStatus: (status: CancelRequestStatus): Promise<LeaveCancelRequest[]> => {
    return apiClient.get<LeaveCancelRequest[]>(`/attendance/cancel-requests?status=${status}`);
  },

  approveCancelRequest: (id: number, approver: string): Promise<LeaveCancelRequest> => {
    return apiClient.patch<LeaveCancelRequest>(`/attendance/cancel-requests/${id}/approve`, { approver });
  },

  rejectCancelRequest: (id: number, rejectReason: string): Promise<LeaveCancelRequest> => {
    return apiClient.patch<LeaveCancelRequest>(`/attendance/cancel-requests/${id}/reject`, { rejectReason });
  },
};